import { Controller } from "@hotwired/stimulus";
import { acceptListToString, isValidType } from "../javascripts/is_valid_type";
import heic2any from "heic2any";

// Connects to data-controller="image-upload"
export default class extends Controller {
  static targets = ["button", "file", "preview", "removeImage", "logoImageCache", "coverImageCache"];

  /**
   * img要素から元になったFileを辿れるように保持する
   * @type {Map<HTMLImageElement, File>}
   */
  #imageToFile = new Map();

  connect() {
    this.hideEmptyPreviews();
    this.fileTargets.forEach((fileInput, index) => {
      fileInput.addEventListener("change", this.#validate);
      fileInput.addEventListener("change", this.updatePreview.bind(this, index));
    });
    for (const img of this.previewTargets) {
      img.addEventListener("error", this.#handleImageError);
    }
  }

  hideEmptyPreviews() {
    this.previewTargets.forEach((previewImage) => {
      if (!previewImage.src || previewImage.src.trim() === "" || previewImage.src === window.location.href) {
        const imageItem = previewImage.closest(".file-upload-image__item");
        if (imageItem) {
          imageItem.style.display = "none";
        }
      }
    });
  }

  trigger() {
    const availableFileInput = this.fileTargets.find((_, index) => {
      const previewImage = this.previewTargets[index];
      const src = previewImage.getAttribute("src");
      return !src || src.trim() === "";
    });

    if (availableFileInput) {
      availableFileInput.click();
    }
  }

  delete(event) {
    event.preventDefault();
    this.previewTarget.src = "";
    this.previewTarget.closest(".file-upload-image").setAttribute("aria-hidden", "true");
    this.removeImageTarget.value = "1";
    this.fileTarget.value = "";
    this.updateButtonState();
  }

  delete_cache(event) {
    const targetType = event.currentTarget.dataset.targetType; // "logo" or "cover"
    event.preventDefault();
    this.previewTarget.src = "";
    this.previewTarget.closest(".file-upload-image").setAttribute("aria-hidden", "true");
    this.removeImageTarget.value = "1";
    if (targetType === "logo") {
      this.logoImageCacheTarget.value = "";
    } else if (targetType === "cover") {
      this.coverImageCacheTarget.value = "";
    }
    this.fileTarget.value = "";
    this.updateButtonState();
  }

  deletePlural(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const imageItem = button.closest(".file-upload-image__item");
    const fileInput = imageItem.querySelector('input[type="file"]');
    const index = Array.from(this.fileTargets).indexOf(fileInput);
    this.buttonTarget.setAttribute("aria-disabled", "false");

    if (index !== -1) {
      this.previewTargets[index].src = "";
      this.previewTargets[index].closest(".file-upload-image__item").style.display = "none";
      this.previewTargets[index].closest(".file-upload-image__item").setAttribute("aria-hidden", "true");
      this.removeImageTargets[index].value = "1";
      this.fileTargets[index].value = "";
      this.updateButtonState();
    }
  }

  updatePreview(index) {
    const fileInput = this.fileTargets[index];
    const previewImage = this.previewTargets[index];
    const imageItem = previewImage.closest(".file-upload-image__item");
    const removeImageInput = this.removeImageTargets[index];

    removeImageInput.value = "0";
    const file = fileInput.files[0];
    if (file) {
      this.#imageToFile.set(previewImage, file);
      const url = URL.createObjectURL(file);
      previewImage.src = url;
      imageItem.style.display = "";
      previewImage.closest(".file-upload-image").setAttribute("aria-hidden", "false");
    }
    this.updateButtonState();
  }

  updateButtonState() {
    // 既存の画像と追加した画像を数えるためpreviewTargetsを使う
    // img.srcだとURLが返るので、getAttribute("src")で設定値を得る
    const allFilled = this.previewTargets.every((img) => img.getAttribute("src"));
    this.buttonTarget.setAttribute("aria-disabled", allFilled.toString());
  }

  #validate = (e) => {
    const input = e.target;
    const file = input.files[0];
    if (!file) return;

    if (!this.#isValid(file, input)) {
      input.value = null;
      e.preventDefault();
    }
  };

  #isValid(file, input) {
    // acceptと比較
    const accept = input.accept;
    if (!isValidType(file, accept)) {
      input.value = ""; // 入力をクリア
      const acceptList = acceptListToString(accept);
      alert(`ファイル形式は${acceptList} 形式の画像を設定してください。`);
      return false;
    }

    // サイズ制限
    const maxSize = input.dataset.maxSize;
    const size = file.size;
    if (maxSize && maxSize < size) {
      const mb = maxSize / 1024 / 1024;
      input.value = ""; // 入力をクリア
      alert(`${mb}MB以内でアップロードしてください。`);
      return false;
    }
    return true;
  }

  // 画像の読み込みエラー時、HEICの場合は変換する
  #handleImageError = async (e) => {
    console.debug("#handleImageError", e);
    const img = e.target;
    const file = this.#imageToFile.get(img);
    if (!file) return;
    const filename = file.name;
    if (!filename) return;
    if (!filename.toLowerCase().endsWith(".heic")) return;

    this.#imageToFile.delete(img);
    // 壊れた画像表示を消す
    img.src = `data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg"><rect fill="white" width="100%" height="100%"/></svg>`,
    )}`;

    const heic = await heic2any({ blob: file });
    img.src = URL.createObjectURL(heic);
  };
}
